import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Save, Plus, Trash2, Image as ImageIcon, MapPin, Sparkles, Wallet, ArrowRight, CheckCircle2, X, Loader2 } from "lucide-react";
import { serviceLabel, SERVICE_KEYS } from "@/lib/pricing";
import { useAuth } from "@/lib/auth";
import MarketplaceTab from "@/pages/MarketplaceTab";
import AuditLog from "@/components/AuditLog";

export default function Settings() {
  const [company, setCompany] = useState(null);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "business";

  useEffect(() => {
    api.get("/companies/me").then(({ data }) => setCompany(data));
  }, []);

  const save = async (patch) => {
    try {
      const { data } = await api.patch("/companies/me", patch);
      setCompany(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  if (!company) return <AppShell title="Settings"><div className="text-slate-500 text-sm">Loading…</div></AppShell>;

  return (
    <AppShell title="Settings" subtitle="Configure how your funnel looks and prices" company={company}>
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-xl p-1 h-auto flex-wrap">
          <TabsTrigger value="business" data-testid="tab-business" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Business</TabsTrigger>
          <TabsTrigger value="capture" data-testid="tab-capture" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Capture mode</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Pricing</TabsTrigger>
          <TabsTrigger value="mesh" data-testid="tab-mesh" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Mesh</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Branding</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="tab-marketplace" className="rounded-lg data-[state=active]:bg-[#0F172A] data-[state=active]:text-white px-4 py-2">
            Marketplace <span className="ml-1.5 text-[9px] font-extrabold uppercase tracking-widest text-[#C75D33] data-[state=active]:text-white">New</span>
          </TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking" className="rounded-lg data-[state=active]:bg-[#0F172A] data-[state=active]:text-white px-4 py-2">
            Tracking <span className="ml-1.5 text-[9px] font-extrabold uppercase tracking-widest text-[#C75D33] data-[state=active]:text-white">New</span>
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account" className="rounded-lg data-[state=active]:bg-[#0F172A] data-[state=active]:text-white px-4 py-2">
            Account <span className="ml-1.5 text-[9px] font-extrabold uppercase tracking-widest text-[#C75D33] data-[state=active]:text-white">New</span>
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Billing</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Activity log</TabsTrigger>
          <TabsTrigger value="future" data-testid="tab-future" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2">Coming soon</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="mt-6">
          <BusinessTab company={company} onSave={save} />
        </TabsContent>
        <TabsContent value="capture" className="mt-6">
          <CaptureTab company={company} onSave={save} />
        </TabsContent>
        <TabsContent value="pricing" className="mt-6">
          <PricingTab company={company} onSave={save} />
        </TabsContent>
        <TabsContent value="mesh" className="mt-6">
          <MeshTab company={company} onSave={save} setCompany={setCompany} />
        </TabsContent>
        <TabsContent value="branding" className="mt-6">
          <BrandingTab company={company} onSave={save} setCompany={setCompany} />
        </TabsContent>
        <TabsContent value="marketplace" className="mt-6">
          <MarketplaceTab />
        </TabsContent>
        <TabsContent value="tracking" className="mt-6">
          <TrackingTab company={company} onSave={save} />
        </TabsContent>
        <TabsContent value="account" className="mt-6">
          <AccountTab />
        </TabsContent>
        <TabsContent value="billing" className="mt-6">
          <BillingTab />
        </TabsContent>
        <TabsContent value="activity" className="mt-6">
          <AuditLog mode="company" />
        </TabsContent>
        <TabsContent value="future" className="mt-6">
          <FutureTab company={company} onSave={save} />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
      {desc && <p className="text-sm text-slate-500 mt-1 mb-4">{desc}</p>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function FieldRow({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function BusinessTab({ company, onSave }) {
  const [name, setName] = useState(company.business_name);
  const [info, setInfo] = useState(company.business_info || {});

  return (
    <div className="space-y-6">
      <Section title="Business info">
        <FieldRow label="Business name">
          <Input data-testid="set-business-name" className="h-11 rounded-xl" value={name} onChange={(e) => setName(e.target.value)} />
        </FieldRow>
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldRow label="Phone"><Input className="h-11 rounded-xl" value={info.phone || ""} onChange={(e) => setInfo({ ...info, phone: e.target.value })} /></FieldRow>
          <FieldRow label="Email"><Input className="h-11 rounded-xl" value={info.email || ""} onChange={(e) => setInfo({ ...info, email: e.target.value })} /></FieldRow>
          <FieldRow label="Website"><Input className="h-11 rounded-xl" value={info.website || ""} onChange={(e) => setInfo({ ...info, website: e.target.value })} /></FieldRow>
          <FieldRow label="Service area"><Input className="h-11 rounded-xl" value={info.service_area || ""} onChange={(e) => setInfo({ ...info, service_area: e.target.value })} /></FieldRow>
        </div>
        <FieldRow label="Address"><Textarea className="rounded-xl" value={info.address || ""} onChange={(e) => setInfo({ ...info, address: e.target.value })} /></FieldRow>
        <Button data-testid="save-business-btn" onClick={() => onSave({ business_name: name, business_info: info })} className="bg-[#0F172A] hover:bg-black rounded-xl">
          <Save className="w-4 h-4 mr-1.5" /> Save
        </Button>
      </Section>

      <SlugChangeSection company={company} />
    </div>
  );
}

/* -------------------------------------------------------------------------
 * SlugChangeSection — self-service public URL rename
 *
 * Business context: a company's public quote URL is /:slug/quote. Changing
 * it is a big deal because links on business cards / FB ads / Google-indexed
 * pages could all break. The backend keeps `slug_history` so old URLs
 * auto-redirect to the new slug forever, but we still want the owner to
 * pause before pulling the trigger — hence the confirm modal.
 * ------------------------------------------------------------------------- */
function SlugChangeSection({ company }) {
  const [showModal, setShowModal] = useState(false);
  const currentSlug = company?.slug || "";
  const lastChanged = company?.slug_changed_at;
  const daysSince = lastChanged
    ? Math.floor((Date.now() - new Date(lastChanged).getTime()) / (24 * 60 * 60 * 1000))
    : null;
  const cooldownRemaining = daysSince != null && daysSince < 30 ? 30 - daysSince : 0;

  return (
    <>
      <Section title="Public quote page URL" desc="This is the link homeowners visit to get an instant quote. Change it carefully — old links redirect automatically, but you can only rename it once per 30 days.">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-mono text-slate-800 truncate" data-testid="current-slug-display">
            <span className="text-slate-400">https://windowscreenquote.com</span>
            <span className="font-bold">/{currentSlug}/quote</span>
          </div>
          <Button
            variant="outline"
            className="rounded-xl h-11 shrink-0"
            onClick={() => setShowModal(true)}
            data-testid="open-slug-change-btn"
            disabled={cooldownRemaining > 0}
          >
            Change URL
          </Button>
        </div>
        {cooldownRemaining > 0 && (
          <p className="text-xs text-amber-700 mt-2">
            You can change this again in <strong>{cooldownRemaining} day{cooldownRemaining !== 1 ? "s" : ""}</strong>.
          </p>
        )}
        {company?.slug_history?.length > 0 && (
          <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
            <span className="font-semibold text-slate-900">Auto-redirecting from:</span>{" "}
            {company.slug_history.map((s, i) => (
              <span key={s} className="font-mono">
                {i > 0 && ", "}
                /{s}
              </span>
            ))}
          </div>
        )}
      </Section>

      {showModal && (
        <SlugChangeModal
          currentSlug={currentSlug}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

function SlugChangeModal({ currentSlug, onClose }) {
  const [newSlug, setNewSlug] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const normalized = newSlug.trim().toLowerCase();
  const isValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized) && normalized.length >= 3 && normalized.length <= 40;
  const isDifferent = normalized && normalized !== currentSlug.toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid || !isDifferent || !confirmed) return;
    setSaving(true);
    try {
      await api.post("/companies/me/change-slug", { new_slug: normalized });
      toast.success("URL changed successfully");
      // Hard reload so the whole page (including sidebar links) reflects the new slug.
      window.location.reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't rename URL");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        data-testid="slug-change-modal"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display text-lg font-extrabold">Change your quote page URL</div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 inline-flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 leading-relaxed">
            <strong>Heads-up:</strong> your old URL <span className="font-mono text-xs bg-white px-1 rounded">/{currentSlug}/quote</span> will keep working forever (we auto-redirect it to your new one). But you can only change it once per 30 days, so make it count.
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">New URL</div>
            <div className="mt-1.5 flex items-center gap-0 rounded-xl border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-slate-900">
              <span className="pl-3 pr-1 py-2.5 text-xs text-slate-500 font-mono bg-slate-50">windowscreenquote.com/</span>
              <Input
                autoFocus
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="my-screen-co"
                className="border-0 h-11 rounded-none font-mono focus-visible:ring-0 flex-1"
                maxLength={40}
                data-testid="new-slug-input"
              />
              <span className="pr-3 pl-1 py-2.5 text-xs text-slate-500 font-mono bg-slate-50">/quote</span>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              Lowercase letters, digits, and hyphens only. 3–40 characters.
            </p>
          </div>
          <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(!!v)}
              data-testid="confirm-slug-checkbox"
              className="mt-0.5"
            />
            <span className="text-sm text-slate-700 leading-snug">
              I understand this changes my public quote URL. Business cards, ads, and any places I&apos;ve shared the old URL will redirect automatically.
            </span>
          </label>
        </div>
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl h-10">Cancel</Button>
          <Button
            type="submit"
            disabled={!isValid || !isDifferent || !confirmed || saving}
            data-testid="confirm-slug-change-btn"
            className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 font-bold disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change URL"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function PricingTab({ company, onSave }) {
  const [s, setS] = useState(company.settings);
  const setSvc = (k, p) => setS({ ...s, services: { ...s.services, [k]: { ...s.services[k], ...p } } });

  const updateTier = (i, key, val) => {
    const tiers = [...s.bulk_discount_tiers];
    tiers[i] = { ...tiers[i], [key]: parseFloat(val) || 0 };
    setS({ ...s, bulk_discount_tiers: tiers });
  };

  return (
    <div className="space-y-6">
      <Section title="Service pricing" desc="Per-screen prices customers see in the estimate funnel. Toggle Taxable on/off per service — only applied when you've enabled Charge Tax below.">
        <div
          data-testid="pricing-variable-price-tip"
          className="rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4 flex gap-3"
        >
          <div className="text-amber-600 mt-0.5 shrink-0" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></svg>
          </div>
          <div className="text-sm text-amber-900 leading-relaxed">
            <span className="font-semibold">Pricing tip — variable jobs.</span> If your price varies by screen size,
            enter your <span className="font-semibold">highest price</span> or your <span className="font-semibold">average</span>.
            You can always discount on-site, but it&apos;s much harder to raise the price after a customer has seen a number.
            <span className="block mt-1 text-amber-800/90">
              Example: if you charge $40–$60 for a window rescreen, enter <span className="font-semibold">$50 (average)</span> or <span className="font-semibold">$60 (highest)</span>.
            </span>
          </div>
        </div>
        {SERVICE_KEYS.map((k) => {
          const cfg = s.services[k] || { enabled: false, price_per_unit: 0, taxable: false };
          const taxable = cfg.taxable !== false;
          return (
            <div key={k} className="rounded-xl border border-slate-200 p-3 space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Switch checked={cfg.enabled} onCheckedChange={(v) => setSvc(k, { enabled: v })} data-testid={`set-svc-${k}-toggle`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{serviceLabel(k)}</div>
                  <div className="text-xs text-slate-500">{cfg.enabled ? "Visible in funnel" : "Hidden"}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => setSvc(k, { taxable: !taxable })}
                  data-testid={`set-svc-${k}-taxable`}
                  className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors ${
                    taxable
                      ? "bg-[#C75D33]/10 text-[#C75D33] border-[#C75D33]/30"
                      : "bg-slate-100 text-slate-500 border-slate-200"
                  }`}
                  title={taxable ? "Tax applies (when Charge Tax is on)" : "Never taxed"}
                >
                  {taxable ? "Taxable" : "Tax-exempt"}
                </button>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">$</span>
                  <Input
                    data-testid={`set-svc-${k}-price`}
                    type="number"
                    value={cfg.price_per_unit}
                    onChange={(e) => setSvc(k, { price_per_unit: parseFloat(e.target.value) || 0 })}
                    className="h-10 w-24 rounded-lg text-right"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </Section>

      <Section title="Fees & taxes">
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldRow label="Minimum service fee" hint="Applied if estimate falls below this amount.">
            <div className="flex items-center gap-1"><span>$</span>
              <Input type="number" value={s.minimum_service_fee} onChange={(e) => setS({ ...s, minimum_service_fee: parseFloat(e.target.value) || 0 })} className="h-11 rounded-xl" /></div>
          </FieldRow>
          <FieldRow label="Trip fee" hint="Flat fee added per estimate.">
            <div className="flex items-center gap-1"><span>$</span>
              <Input type="number" value={s.trip_fee} onChange={(e) => setS({ ...s, trip_fee: parseFloat(e.target.value) || 0 })} className="h-11 rounded-xl" /></div>
          </FieldRow>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
          <Switch checked={s.tax_enabled} onCheckedChange={(v) => setS({ ...s, tax_enabled: v })} data-testid="set-tax-toggle" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">Charge tax</div>
            <div className="text-xs text-slate-500">Tax rate (e.g. 0.0875 for 8.75%)</div>
          </div>
          <Input type="number" step="0.0001" value={s.tax_rate} onChange={(e) => setS({ ...s, tax_rate: parseFloat(e.target.value) || 0 })} className="h-10 w-28 rounded-lg text-right" />
        </div>
      </Section>

      <Section title="Bulk discounts" desc="Percentage discount when total quantity hits each tier.">
        {s.bulk_discount_tiers.map((t, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
            <div className="text-sm text-slate-700 font-medium flex-1">
              Min qty <Input type="number" value={t.min_qty} onChange={(e) => updateTier(i, "min_qty", e.target.value)} className="inline-block h-9 w-20 mx-2 rounded-lg" />
            </div>
            <div className="flex items-center gap-1">
              <Input type="number" value={t.percent} onChange={(e) => updateTier(i, "percent", e.target.value)} className="h-9 w-20 rounded-lg text-right" />
              <span className="text-slate-500">%</span>
            </div>
          </div>
        ))}
      </Section>

      <Button onClick={() => onSave({ settings: s })} className="bg-[#0F172A] hover:bg-black rounded-xl" data-testid="save-pricing-btn">
        <Save className="w-4 h-4 mr-1.5" /> Save pricing
      </Button>
    </div>
  );
}

function MeshTab({ company, onSave, setCompany }) {
  const [mesh, setMesh] = useState(company.settings.mesh_upgrades || []);
  const fileRefs = useRef({});
  const [uploadingId, setUploadingId] = useState(null);
  const update = (i, patch) => setMesh(mesh.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const remove = (i) => {
    if (!window.confirm(`Remove "${mesh[i].name}"?`)) return;
    setMesh(mesh.filter((_, j) => j !== i));
  };
  const addNew = () => {
    setMesh([
      ...mesh,
      {
        id: `new-${Date.now()}`,
        name: "New upgrade",
        description: "",
        price_per_screen: 0,
        door_price_per_screen: 0,
        show_on_windows: true,
        show_on_doors: true,
        image_url: "",
        enabled: true,
        is_default: false,
      },
    ]);
  };

  const uploadImage = async (mesh_id, file, i) => {
    if (!file) return;
    if (String(mesh_id).startsWith("new-")) {
      toast.error("Save mesh options first, then upload an image for this new upgrade.");
      return;
    }
    setUploadingId(mesh_id);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/companies/me/mesh/${mesh_id}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update(i, { image_url: data.image_url });
      // Refresh company in parent so any other components see new image
      const { data: refreshed } = await api.get("/companies/me");
      setCompany(refreshed);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <Section title="Mesh upgrades" desc="Add as many upgrade options as you'd like. Add an image, set different pricing and visibility for windows vs sliding doors.">
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
        <Switch
          checked={!!company.settings?.show_mesh_images}
          onCheckedChange={(v) => onSave({ settings: { ...company.settings, mesh_upgrades: mesh, show_mesh_images: v } })}
          data-testid="set-mesh-show-images-toggle"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">Show images on the customer quote page</div>
          <div className="text-xs text-slate-500">Off by default for a compact layout. Turn on to display the thumbnail next to each mesh option.</div>
        </div>
      </div>
      {mesh.map((m, i) => {
        const imgFull = m.image_url?.startsWith("/api") ? `${BACKEND_URL}${m.image_url}` : m.image_url;
        return (
          <div key={m.id} className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Switch checked={m.enabled} onCheckedChange={(v) => update(i, { enabled: v })} data-testid={`set-mesh-${i}-toggle`} className="mt-2.5" />
              <div className="w-16 h-16 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                {imgFull ? (
                  <img src={imgFull} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Input className="h-10 rounded-lg font-semibold" value={m.name} onChange={(e) => update(i, { name: e.target.value })} />
                <div className="flex gap-2">
                  <input
                    ref={(el) => { fileRefs.current[m.id] = el; }}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => uploadImage(m.id, e.target.files?.[0], i)}
                    data-testid={`set-mesh-${i}-file`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileRefs.current[m.id]?.click()}
                    disabled={uploadingId === m.id}
                    className="rounded-lg text-xs"
                    data-testid={`set-mesh-${i}-upload-btn`}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    {uploadingId === m.id ? "Uploading…" : imgFull ? "Replace image" : "Upload image"}
                  </Button>
                  {!m.is_default && (
                    <Button variant="ghost" size="sm" onClick={() => remove(i)} className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto" data-testid={`set-mesh-${i}-remove`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <Textarea className="rounded-lg" value={m.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="Description shown in funnel" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Window screen price</Label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-slate-500">+$</span>
                  <Input type="number" value={m.price_per_screen} onChange={(e) => update(i, { price_per_screen: parseFloat(e.target.value) || 0 })} className="h-10 rounded-lg text-right" disabled={m.is_default} />
                  <span className="text-xs text-slate-400 whitespace-nowrap">/ screen</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Sliding door price</Label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-slate-500">+$</span>
                  <Input type="number" value={m.door_price_per_screen ?? 0} onChange={(e) => update(i, { door_price_per_screen: parseFloat(e.target.value) || 0 })} className="h-10 rounded-lg text-right" disabled={m.is_default} data-testid={`set-mesh-${i}-door-price`} />
                  <span className="text-xs text-slate-400 whitespace-nowrap">/ door</span>
                </div>
              </div>
            </div>
            {!m.is_default && (
              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <Switch checked={m.show_on_windows !== false} onCheckedChange={(v) => update(i, { show_on_windows: v })} data-testid={`set-mesh-${i}-windows`} />
                  Show on window screens
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <Switch checked={m.show_on_doors !== false} onCheckedChange={(v) => update(i, { show_on_doors: v })} data-testid={`set-mesh-${i}-doors`} />
                  Show on sliding doors
                </label>
              </div>
            )}
            {m.is_default && <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Default (included on every estimate)</div>}
          </div>
        );
      })}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <Button onClick={addNew} variant="outline" className="rounded-xl" data-testid="add-mesh-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add upgrade
        </Button>
        <Button onClick={() => onSave({ settings: { ...company.settings, mesh_upgrades: mesh } })} className="bg-[#0F172A] hover:bg-black rounded-xl" data-testid="save-mesh-btn">
          <Save className="w-4 h-4 mr-1.5" /> Save mesh options
        </Button>
      </div>
    </Section>
  );
}

function BrandingTab({ company, onSave, setCompany }) {
  const [b, setB] = useState(company.branding || {});
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (file) => {
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/companies/me/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      const newB = { ...b, logo_url: data.logo_url };
      setB(newB);
      setCompany({ ...company, branding: newB });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const logoFullUrl = b.logo_url?.startsWith("/api") ? `${BACKEND_URL}${b.logo_url}` : b.logo_url;

  return (
    <Section title="Branding" desc="Personalize your white-label estimate funnel.">
      <FieldRow label="Logo" hint="PNG, JPG, or SVG. Max 5MB.">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
            {logoFullUrl ? <img src={logoFullUrl} alt="logo" className="w-full h-full object-contain" /> : <span className="text-slate-400 text-xs">No logo</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="logo-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-xl" data-testid="upload-logo-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload logo"}
          </Button>
        </div>
      </FieldRow>

      <div className="grid sm:grid-cols-2 gap-4">
        <FieldRow label="Primary color">
          <div className="flex items-center gap-3">
            <input type="color" value={b.primary_color || "#0F172A"} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="w-12 h-11 rounded-xl border border-slate-200 cursor-pointer" data-testid="primary-color-input" />
            <Input value={b.primary_color || "#0F172A"} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="h-11 rounded-xl font-mono" />
          </div>
        </FieldRow>
        <FieldRow label="Accent color">
          <div className="flex items-center gap-3">
            <input type="color" value={b.accent_color || "#0F172A"} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="w-12 h-11 rounded-xl border border-slate-200 cursor-pointer" />
            <Input value={b.accent_color || "#0F172A"} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="h-11 rounded-xl font-mono" />
          </div>
        </FieldRow>
      </div>

      <Button onClick={() => onSave({ branding: b })} className="bg-[#0F172A] hover:bg-black rounded-xl" data-testid="save-branding-btn">
        <Save className="w-4 h-4 mr-1.5" /> Save branding
      </Button>
    </Section>
  );
}

function CaptureTab({ company, onSave }) {
  const [mode, setMode] = useState(company.settings?.lead_capture_mode || "max");
  const options = [
    {
      value: "max",
      label: "Maximum Lead Capture",
      tag: "Recommended",
      tagClass: "bg-emerald-100 text-emerald-700",
      sub: "Collect more leads and follow up later.",
      desc: "Customer enters contact info first, then sees the quote. Best for companies who actively follow up with leads via calls, texts, or email.",
      flow: ["Pick services", "Set quantity", "Choose mesh", "Enter contact info", "See quote"],
    },
    {
      value: "high_intent",
      label: "High Intent Leads",
      tag: null,
      sub: "Show pricing first and generate more serious inquiries.",
      desc: "Customer sees the quote up front. They only enter contact info if they want to book. Fewer leads, higher buying intent.",
      flow: ["Pick services", "Set quantity", "Choose mesh", "See quote", "Request service (contact)"],
    },
    {
      value: "hybrid",
      label: "Hybrid Mode",
      tag: "Test mode",
      tagClass: "bg-amber-100 text-amber-700",
      sub: "Show a price range first, then unlock the full quote.",
      desc: "Customer sees an estimated range, then unlocks the exact quote by entering contact info. Balances trust and lead capture.",
      flow: ["Pick services", "Set quantity", "Choose mesh", "See price range", "Enter contact info", "See exact quote"],
    },
  ];

  return (
    <Section title="Lead Capture Mode" desc="Controls when homeowners see pricing on your quote page. Pick the style that matches how your team follows up.">
      <div className="space-y-3">
        {options.map((opt) => {
          const selected = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setMode(opt.value)}
              data-testid={`capture-mode-${opt.value}`}
              className={`w-full text-left rounded-2xl border-2 p-5 transition-all ${
                selected ? "border-[#C75D33] bg-[#C75D33]/5" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${selected ? "border-[#C75D33]" : "border-slate-300"}`}>
                  {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#0F172A]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-slate-900">{opt.label}</span>
                    {opt.tag && <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${opt.tagClass}`}>{opt.tag}</span>}
                  </div>
                  <div className="text-sm text-slate-700 mt-1 font-medium">{opt.sub}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">{opt.desc}</div>
                  <div className="text-[11px] text-slate-400 mt-3 flex flex-wrap items-center gap-1">
                    {opt.flow.map((s, i) => (
                      <span key={i} className="flex items-center gap-1">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{i + 1}. {s}</span>
                        {i < opt.flow.length - 1 && <span className="text-slate-300">›</span>}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <Button
        onClick={() => onSave({ settings: { ...company.settings, lead_capture_mode: mode } })}
        className="bg-[#0F172A] hover:bg-black rounded-xl"
        data-testid="save-capture-btn"
      >
        <Save className="w-4 h-4 mr-1.5" /> Save capture mode
      </Button>
    </Section>
  );
}

function FutureTab({ company, onSave }) {
  const [flags, setFlags] = useState(company.feature_flags || {});
  // True roadmap items only — Marketplace, Lead purchasing, and Subscription billing
  // already shipped, so they no longer belong in "Coming soon".
  const items = [
    {
      key: "sms_enabled",
      title: "SMS lead alerts",
      desc: "Get a text the moment a marketplace lead lands in your dashboard. Code is live — waiting on toll-free carrier approval.",
      eta: "Weeks",
    },
    {
      key: "custom_domain_enabled",
      title: "Custom domain for your funnel",
      desc: "Use your own domain (e.g., quote.yourcompany.com) instead of the windowscreenquote.com subdomain.",
      eta: "Q2",
    },
  ];
  return (
    <div className="space-y-5">
      {/* What's already shipped — quick reassurance */}
      <Section title="Now live" desc="Features that already shipped. Tap to open the tab.">
        <div className="grid sm:grid-cols-3 gap-2">
          <Link to="/settings?tab=marketplace" className="block rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors p-3" data-testid="future-live-marketplace">
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">
              <CheckCircle2 className="w-3 h-3" /> Live
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-1">Marketplace lead routing</div>
            <div className="text-xs text-slate-600 mt-0.5">Exclusive ZIP-routed leads, capped at $50.</div>
          </Link>
          <Link to="/marketplace" className="block rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors p-3" data-testid="future-live-wallet">
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">
              <CheckCircle2 className="w-3 h-3" /> Live
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-1">Prepaid wallet + auto-reload</div>
            <div className="text-xs text-slate-600 mt-0.5">Top up once, never miss a lead.</div>
          </Link>
          <Link to="/billing" className="block rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors p-3" data-testid="future-live-billing">
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">
              <CheckCircle2 className="w-3 h-3" /> Live
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-1">Subscription billing</div>
            <div className="text-xs text-slate-600 mt-0.5">Stripe-backed. Manage card, invoices, cancel anytime.</div>
          </Link>
        </div>
      </Section>

      <Section title="Coming soon" desc="What we're building next. Toggle to express interest — we'll email you the moment it ships.">
        {items.map((it) => (
          <div key={it.key} data-testid={`future-item-${it.key}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
            <Switch
              data-testid={`future-toggle-${it.key}`}
              checked={!!flags[it.key]}
              onCheckedChange={(v) => setFlags({ ...flags, [it.key]: v })}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{it.title}</div>
              <div className="text-xs text-slate-500 leading-relaxed">{it.desc}</div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-amber-600 font-bold whitespace-nowrap">{it.eta}</span>
          </div>
        ))}
        <Button
          data-testid="future-save-btn"
          onClick={() => onSave({ feature_flags: flags })}
          className="bg-slate-900 hover:bg-slate-800 rounded-xl"
        >
          Save interest
        </Button>
      </Section>
    </div>
  );
}

function BillingTab() {
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    api.get("/billing/me").then(({ data }) => setBilling(data));
  }, []);
  if (!billing) return <div className="text-slate-500 text-sm">Loading…</div>;
  const sub = billing.subscription || { status: "inactive" };
  const plan = billing.plan;
  const isActive = sub.status === "active" || sub.status === "trialing";
  return (
    <Section title="Subscription" desc="Manage your WindowScreenQuote subscription.">
      <div className="rounded-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Current plan</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{plan?.name || "—"}</div>
            <div className="text-sm text-slate-600 mt-1">
              Status: <span className={`font-semibold ${isActive ? "text-emerald-600" : "text-amber-600"}`}>{sub.status}</span>
              {plan && <> · ${plan.monthly_amount}/mo</>}
            </div>
            {sub.status === "trialing" && sub.trial_end && (
              <div className="text-xs text-slate-500 mt-1">Trial ends {new Date(sub.trial_end).toLocaleDateString()}</div>
            )}
            {sub.status === "active" && sub.current_period_end && (
              <div className="text-xs text-slate-500 mt-1">Next bill {new Date(sub.current_period_end).toLocaleDateString()}</div>
            )}
          </div>
          <Link to="/billing" data-testid="settings-manage-billing">
            <Button variant="outline" className="rounded-xl">Manage billing</Button>
          </Link>
        </div>
      </div>
    </Section>
  );
}

/* =========================================================================
 * TrackingTab — per-company FB Pixel + Google Ads pixel setup
 *
 * Why this exists: each screen company runs their own Facebook / Google Ads
 * campaigns pointing at their tenant funnel. They need conversion data going
 * into THEIR ad accounts (not the platform's) for accurate ROAS.
 *
 * Server-side validation (TrackingPixels in models.py) enforces strict regex
 * on each ID so we can safely inject the value into a pixel snippet.
 * ========================================================================= */
function TrackingTab({ company, onSave }) {
  const initial = company?.tracking || {};
  const [fb, setFb] = useState(initial.fb_pixel_id || "");
  const [gadsId, setGadsId] = useState(initial.google_ads_conversion_id || "");
  const [gadsLabel, setGadsLabel] = useState(initial.google_ads_conversion_label || "");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diag, setDiag] = useState(null);

  const persist = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await onSave({
        tracking: {
          fb_pixel_id: fb.trim(),
          google_ads_conversion_id: gadsId.trim(),
          google_ads_conversion_label: gadsLabel.trim(),
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!fb && !(gadsId && gadsLabel)) {
      toast.error("Add at least one Pixel ID before sending a test");
      return;
    }
    setTesting(true);
    try {
      // Dynamic import keeps the tenant-pixel SDK out of the main bundle for
      // non-tracking pages.
      const mod = await import("@/lib/tenantPixels");
      mod.fireTenantTestEvent({
        fb_pixel_id: fb.trim(),
        google_ads_conversion_id: gadsId.trim(),
        google_ads_conversion_label: gadsLabel.trim(),
      });
      toast.success("Test conversion fired — check Meta Events Manager / Google Tag Assistant in ~30s");
    } finally {
      // Show the spinner just long enough that it feels deliberate.
      setTimeout(() => setTesting(false), 1200);
    }
  };

  const runDiagnostics = async () => {
    setDiagnosing(true);
    try {
      const { data } = await api.get(`/tracking/diagnostics`);
      setDiag(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't run diagnostics");
    } finally {
      setDiagnosing(false);
    }
  };

  return (
    <form onSubmit={persist} className="space-y-6">
      <Section title="Conversion pixels on your quote funnel" desc="Send conversion events into your own Facebook + Google Ads accounts so you can measure ROAS on your ad spend.">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 leading-relaxed">
          <strong className="text-slate-900">How it works:</strong> when a homeowner submits a quote on your funnel at <span className="font-mono text-xs bg-white border border-slate-200 rounded px-1.5 py-0.5">/{company?.slug || "your-slug"}/quote</span>, we fire a <strong>Lead</strong> event (Facebook) and a <strong>Conversion</strong> event (Google Ads) — with the quote $ value attached — into the accounts you configure below. Nothing fires until you save valid IDs here.
        </div>

        <FieldRow label="Facebook Pixel ID">
          <Input
            value={fb}
            onChange={(e) => setFb(e.target.value)}
            placeholder="15- to 17-digit number, e.g. 1234567890123456"
            inputMode="numeric"
            data-testid="tracking-fb-pixel-input"
            className="h-11 rounded-xl font-mono"
          />
          <p className="text-xs text-slate-500 mt-1.5">
            Find this in Meta Business → Events Manager → your Pixel → Settings. Pixel IDs are digits only (usually 15–17 characters).{" "}
            <a href="https://www.facebook.com/business/help/952192354843755" target="_blank" rel="noreferrer" className="text-[#C75D33] underline-offset-2 hover:underline">Where do I find it?</a>
          </p>
        </FieldRow>

        <FieldRow label="Google Ads Conversion ID">
          <Input
            value={gadsId}
            onChange={(e) => setGadsId(e.target.value)}
            placeholder="AW-1234567890"
            data-testid="tracking-gads-id-input"
            className="h-11 rounded-xl font-mono"
          />
          <p className="text-xs text-slate-500 mt-1.5">Format: <span className="font-mono">AW-</span> followed by your account ID.</p>
        </FieldRow>

        <FieldRow label="Google Ads Conversion Label">
          <Input
            value={gadsLabel}
            onChange={(e) => setGadsLabel(e.target.value)}
            placeholder="e.g. AbC-D_efGhIjK1L2"
            data-testid="tracking-gads-label-input"
            className="h-11 rounded-xl font-mono"
          />
          <p className="text-xs text-slate-500 mt-1.5">
            In Google Ads → Tools → Conversions → your action → tag setup, copy the part AFTER the slash in <span className="font-mono">AW-XXXX/<strong>THIS_PART</strong></span>.
          </p>
        </FieldRow>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button
            type="submit"
            disabled={saving}
            data-testid="tracking-save-btn"
            className="bg-[#0F172A] hover:bg-black rounded-xl h-10 font-bold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            data-testid="tracking-test-btn"
            className="rounded-xl h-10 font-bold"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Send test event</>}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={runDiagnostics}
            disabled={diagnosing}
            data-testid="tracking-diagnose-btn"
            className="rounded-xl h-10 font-bold"
          >
            {diagnosing ? <Loader2 className="w-4 h-4 animate-spin" /> : <>🩺 Diagnose</>}
          </Button>
          <span className="text-xs text-slate-500 ml-2">Test fires a $99.99 sentinel conversion you can spot in Events Manager.</span>
        </div>

        {diag && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="tracking-diagnostics">
            <div className={`px-4 py-3 border-b border-slate-200 flex items-center justify-between ${diag.summary_ok ? "bg-emerald-50" : "bg-amber-50"}`}>
              <div className="font-bold text-sm text-slate-900">
                {diag.summary_ok ? "✅ Pixel setup looks healthy" : "⚠️ Something looks off — see below"}
              </div>
              <button type="button" onClick={() => setDiag(null)} className="text-xs text-slate-500 hover:text-slate-900 underline">
                Close
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[11px] text-slate-500">
                <div><span className="font-semibold text-slate-700">Public funnel:</span> <a href={diag.company.public_url} target="_blank" rel="noreferrer" className="text-[#C75D33] underline">{diag.company.public_url}</a></div>
                <div><span className="font-semibold text-slate-700">Subscription:</span> {diag.company.subscription_status}</div>
              </div>
              <ul className="space-y-2">
                {diag.checks.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 text-sm" data-testid={`diag-check-${c.id}`}>
                    <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center text-[11px] font-bold ${
                      c.status === "ok" ? "bg-emerald-100 text-emerald-700"
                      : c.status === "fail" ? "bg-red-100 text-red-700"
                      : c.status === "warn" ? "bg-amber-100 text-amber-700"
                      : c.status === "info" ? "bg-slate-100 text-slate-600"
                      : "bg-slate-50 text-slate-400"
                    }`}>
                      {c.status === "ok" ? "✓" : c.status === "fail" ? "✕" : c.status === "warn" ? "!" : c.status === "info" ? "i" : "–"}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800">{c.label}</div>
                      <div className="text-xs text-slate-600 leading-snug break-words">{c.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </Section>

      <Section title="What we send" desc="Both pixels receive the homeowner's quote $ total as the conversion value so your ROAS column shows real revenue, not a flat $1.">
        <div className="grid sm:grid-cols-2 gap-3">
          <PixelEventCard
            platform="Facebook"
            events={[
              { name: "PageView", when: "Every homeowner visit" },
              { name: "Lead", when: "Quote submitted · value = quote $" },
            ]}
          />
          <PixelEventCard
            platform="Google Ads"
            events={[
              { name: "Page load", when: "Every homeowner visit" },
              { name: "Conversion", when: "Quote submitted · value = quote $" },
            ]}
          />
        </div>
      </Section>
    </form>
  );
}

function PixelEventCard({ platform, events }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">{platform}</div>
      <ul className="mt-2 space-y-2">
        {events.map((e) => (
          <li key={e.name} className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <span className="font-semibold text-slate-900">{e.name}</span>
            <span className="text-slate-500 text-xs">— {e.when}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* =========================================================================
 * AccountTab — display name / password / email change for the logged-in user
 *
 * Companies get bought, sold, and rebranded. Owners need self-service ways
 * to update their login credentials without pinging support. This tab
 * exposes three endpoints:
 *   PATCH /auth/me                         — display name (trivial)
 *   POST  /auth/change-password            — needs current pw, invalidates
 *                                             other sessions
 *   POST  /auth/request-email-change       — sends 6-digit code to NEW email
 *   POST  /auth/confirm-email-change       — validates code, flips email,
 *                                             invalidates other sessions
 *
 * Session invalidation is transparent to the user on THIS device — the
 * backend re-issues a fresh cookie/JWT on the response, so we don't need
 * to relogin them here.
 * ========================================================================= */
function AccountTab() {
  const { user, refresh } = useAuth();
  return (
    <div className="space-y-6" data-testid="account-tab">
      <NameSection user={user} onSaved={refresh} />
      <EmailSection user={user} onSaved={refresh} />
      <PasswordSection />
    </div>
  );
}

function NameSection({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      toast.success("Name updated");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Section title="Display name" desc="Shown on your account, audit logs, and any emails you send from the platform.">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="account-name-input"
            className="h-11 rounded-xl"
            maxLength={80}
          />
        </div>
        <Button
          type="submit"
          disabled={saving || name.trim() === (user?.name || "")}
          data-testid="account-name-save-btn"
          className="bg-[#0F172A] hover:bg-black rounded-xl h-11 font-bold disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
        </Button>
      </form>
    </Section>
  );
}

function EmailSection({ user, onSaved }) {
  const [stage, setStage] = useState("idle"); // idle | request | confirm
  const [newEmail, setNewEmail] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);

  const startChange = () => setStage("request");
  const cancel = () => { setStage("idle"); setNewEmail(""); setCurrentPw(""); setCode(""); };

  const requestChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/request-email-change", { current_password: currentPw, new_email: newEmail.trim().toLowerCase() });
      toast.success("6-digit code sent — check your NEW email inbox");
      setCurrentPw("");
      setStage("confirm");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't request change");
    } finally {
      setSaving(false);
    }
  };

  const confirmChange = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/confirm-email-change", { code: code.trim() });
      toast.success("Login email updated");
      cancel();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't confirm change");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Login email" desc="The email address you use to sign in. Changing this requires verifying the new email.">
      {stage === "idle" && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-mono text-slate-800 truncate" data-testid="account-current-email">
            {user?.email}
          </div>
          <Button
            variant="outline"
            className="rounded-xl h-11 shrink-0"
            onClick={startChange}
            data-testid="account-change-email-btn"
          >
            Change email
          </Button>
        </div>
      )}
      {stage === "request" && (
        <form onSubmit={requestChange} className="space-y-3">
          <FieldRow label="New email">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@newdomain.com"
              autoComplete="email"
              data-testid="account-new-email-input"
              className="h-11 rounded-xl"
              required
            />
          </FieldRow>
          <FieldRow label="Confirm current password">
            <Input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              placeholder="Your current password"
              autoComplete="current-password"
              data-testid="account-email-current-pw-input"
              className="h-11 rounded-xl"
              required
            />
          </FieldRow>
          <div className="flex items-center gap-2 pt-1">
            <Button type="button" variant="outline" onClick={cancel} className="rounded-xl h-10">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="account-request-email-btn" className="bg-[#0F172A] hover:bg-black rounded-xl h-10 font-bold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send verification code"}
            </Button>
          </div>
        </form>
      )}
      {stage === "confirm" && (
        <form onSubmit={confirmChange} className="space-y-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            We sent a 6-digit code to <strong className="font-mono">{newEmail}</strong>. Enter it below within 15 minutes.
          </div>
          <FieldRow label="6-digit code">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              data-testid="account-email-code-input"
              className="h-12 rounded-xl font-mono text-2xl tracking-widest text-center"
              autoFocus
              required
            />
          </FieldRow>
          <div className="flex items-center gap-2 pt-1">
            <Button type="button" variant="outline" onClick={cancel} className="rounded-xl h-10">Cancel</Button>
            <Button type="submit" disabled={saving || code.length !== 6} data-testid="account-confirm-email-btn" className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 font-bold disabled:opacity-40">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & switch email"}
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — other devices have been signed out");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change password");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Section title="Password" desc="Update the password you use to log in. Other devices you're signed in on will be logged out.">
      <form onSubmit={submit} className="space-y-3">
        <FieldRow label="Current password">
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" data-testid="account-current-pw-input" className="h-11 rounded-xl" required />
        </FieldRow>
        <div className="grid sm:grid-cols-2 gap-3">
          <FieldRow label="New password" hint="Min 8 characters">
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="account-new-pw-input" className="h-11 rounded-xl" required />
          </FieldRow>
          <FieldRow label="Confirm new password">
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="account-confirm-pw-input" className="h-11 rounded-xl" required />
          </FieldRow>
        </div>
        <Button type="submit" disabled={saving || !current || !next || !confirm} data-testid="account-change-pw-btn" className="bg-[#0F172A] hover:bg-black rounded-xl h-11 font-bold disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Update password</>}
        </Button>
      </form>
    </Section>
  );
}
